import { IFormPageData, ISectionData, IMeshData, IRowData, IColumnData, IColumnElementData, IHiddenFieldsData } from './models/form-page-data';
import { FormStylesData } from './models/styles/form-styles-data';
import { FieldJsonData, WidgetJsonData, HiddenFieldJsonData, RuntimeContext } from './models/element-types';
import { SectionRenderer } from './renderers/grid/section-renderer';
import { MeshRenderer } from './renderers/grid/mesh-renderer';
import { RowRenderer } from './renderers/grid/row-renderer';
import { ColumnRenderer } from './renderers/grid/column-renderer';
import { HiddenBlockRenderer } from './renderers/grid/hidden-block-renderer';
import { HiddenFieldRenderer } from './renderers/fields/hidden-field-renderer';
import { WidgetRendererFactory } from './renderers/widgets/widget-renderer-factory';
import { FieldRendererFactory } from './renderers/fields/field-renderer-factory';
import { SpecialRendererFactory } from './renderers/special/special-renderer-factory';
import { DomQuery } from './utils/dom-query';
import { SelectorUtil } from './utils/selector-util';
import { applyVariablesToPageData, type VariablesMap } from './utils/variable-replacement.utils';

export interface FormRenderOptions {
    runtimeContext?: RuntimeContext;
    baseUrl?: string;
    variables?: VariablesMap;
}

export class FormJsonSerializer {
    private readonly sectionRenderer = new SectionRenderer();
    private readonly meshRenderer = new MeshRenderer();
    private readonly rowRenderer = new RowRenderer();
    private readonly columnRenderer = new ColumnRenderer();
    private readonly hiddenBlockRenderer = new HiddenBlockRenderer();
    private readonly hiddenFieldRenderer = new HiddenFieldRenderer();
    private readonly widgetRendererFactory = new WidgetRendererFactory();
    private readonly fieldRendererFactory = new FieldRendererFactory();
    private readonly specialRendererFactory = new SpecialRendererFactory();

    public toJSON(container: HTMLElement, styles?: FormStylesData): IFormPageData {
        const result: IFormPageData = {
            sections: []
        };

        const sectionElements = DomQuery.getSections(container);
        result.sections = sectionElements.map(section => this.serializeSection(section));

        const hiddenFieldsData = this.serializeHiddenFields(container);
        if (hiddenFieldsData) {
            result.hiddenFields = hiddenFieldsData;
        }

        if (styles !== undefined) {
            result.styles = { ...styles };
        }

        return result;
    }

    private serializeSection(sectionElement: HTMLElement): ISectionData {
        const sectionType = DomQuery.getSectionType(sectionElement) ?? 'form';
        const meshElements = DomQuery.getMeshes(sectionElement);

        return {
            id: sectionElement.id,
            type: sectionType as ISectionData['type'],
            meshes: meshElements.map(mesh => this.serializeMesh(mesh))
        };
    }

    private serializeMesh(meshElement: HTMLElement): IMeshData {
        const meshContainer = DomQuery.getMeshContainer(meshElement);
        const rowElements = meshContainer
            ? Array.from(meshContainer.querySelectorAll<HTMLElement>(':scope > [data-mesh-row]'))
            : [];

        return {
            id: meshElement.id,
            rows: rowElements.map(row => this.serializeRow(row))
        };
    }

    private serializeRow(rowElement: HTMLElement): IRowData {
        const colElements = Array.from(rowElement.querySelectorAll<HTMLElement>(':scope > [data-mesh-col]'));

        return {
            id: rowElement.id,
            columns: colElements.map(col => this.serializeColumn(col))
        };
    }

    private serializeColumn(columnElement: HTMLElement): IColumnData {
        const elements: IColumnElementData[] = [];
        const childElements = DomQuery.getElements(columnElement);

        childElements.forEach(element => {
            const elementData = this.serializeColumnElement(element);
            if (elementData) {
                elements.push(elementData);
            }
        });

        return {
            id: columnElement.id,
            size: DomQuery.getColumnSize(columnElement).toString(),
            elements
        };
    }

    private serializeColumnElement(element: HTMLElement): IColumnElementData | null {
        const jsonData = element.dataset['elementJson'];
        if (!jsonData) return null;

        try {
            const parsed = JSON.parse(jsonData);
            const elementType = element.getAttribute('data-element');

            if (elementType === 'field') {
                return { type: 'field', data: parsed as FieldJsonData };
            } else {
                return { type: 'widget', data: parsed as WidgetJsonData };
            }
        } catch (e) {
            console.error('[toJSON] Failed to parse element JSON:', e);
            return null;
        }
    }

    private serializeHiddenFields(container: HTMLElement): IHiddenFieldsData | null {
        const hiddenContainer = container.querySelector<HTMLElement>(`#${SelectorUtil.hiddenVarsBlockId}`);
        if (!hiddenContainer || hiddenContainer.childElementCount === 0) {
            return null;
        }

        const hiddenElements = DomQuery.getHiddenElements();
        const fields: HiddenFieldJsonData[] = [];

        hiddenElements.forEach(element => {
            const jsonData = element.dataset['elementJson'];
            if (jsonData) {
                try {
                    fields.push(JSON.parse(jsonData) as HiddenFieldJsonData);
                } catch (e) {
                    console.warn('Failed to parse hidden field JSON:', element.id);
                }
            }
        });

        return fields.length > 0 ? { fields } : null;
    }

    private currentRuntimeContext?: RuntimeContext;
    private currentBaseUrl?: string;

    public renderFromJSON(data: IFormPageData, options?: FormRenderOptions): DocumentFragment {
        const fragment = document.createDocumentFragment();

        if (!data?.sections) return fragment;

        this.currentRuntimeContext = options?.runtimeContext;
        this.currentBaseUrl = options?.baseUrl;

        // Apply variable replacement to the data if variables are provided
        const currentVariables = options?.variables;
        const processedData = currentVariables && Object.keys(currentVariables).length > 0
            ? applyVariablesToPageData(data, currentVariables)
            : data;

        processedData.sections.forEach((sectionData: ISectionData) => {
            const sectionElement = this.renderSection(sectionData);
            fragment.appendChild(sectionElement);
        });

        const hiddenBlock = this.renderHiddenFieldsBlock(processedData.hiddenFields);
        if (hiddenBlock) {
            fragment.appendChild(hiddenBlock);
        }

        this.injectSystemFields(fragment, options?.runtimeContext);

        this.currentRuntimeContext = undefined;
        this.currentBaseUrl = undefined;

        return fragment;
    }

    private injectSystemFields(fragment: DocumentFragment, runtimeContext?: RuntimeContext): void {
        if (!runtimeContext) {
            return;
        }

        if (runtimeContext.account?.accountId) {
            const accountIdRenderer = this.specialRendererFactory.getRenderer('accountId');
            const accountIdElement = accountIdRenderer.createNew({ runtimeContext });
            fragment.appendChild(accountIdElement);
        }
    }

    private renderSection(sectionData: ISectionData): HTMLElement {
        const normalizedSectionType = (sectionData.type || 'form').toLowerCase() as ISectionData['type'];
        const sectionElement = this.sectionRenderer.render({
            id: sectionData.id,
            type: 'section',
            sectionType: normalizedSectionType
        });

        sectionData.meshes?.forEach(meshData => {
            const meshElement = this.renderMesh(meshData);
            sectionElement.appendChild(meshElement);
        });

        return sectionElement;
    }

    private renderMesh(meshData: IMeshData): HTMLElement {
        const meshElement = this.meshRenderer.render({
            id: meshData.id,
            type: 'mesh'
        });

        const meshContainer = meshElement.querySelector('[data-mesh-container]');
        if (!meshContainer) return meshElement;

        meshData.rows?.forEach(rowData => {
            const rowElement = this.renderRow(rowData);
            meshContainer.appendChild(rowElement);
        });

        return meshElement;
    }

    private renderRow(rowData: IRowData): HTMLElement {
        const rowElement = this.rowRenderer.render({
            id: rowData.id,
            type: 'row'
        });

        rowData.columns?.forEach(columnData => {
            const columnElement = this.renderColumn(columnData);
            rowElement.appendChild(columnElement);
        });

        const hasElements = rowData.columns?.some(c => c.elements?.length > 0);
        if (hasElements) {
            rowElement.classList.remove('empty');
        }

        DomQuery.updateFirstLastColumnClasses(rowElement);

        return rowElement;
    }

    private renderColumn(columnData: IColumnData): HTMLElement {
        const size = columnData.size ? parseInt(columnData.size, 10) : 12;
        const columnElement = this.columnRenderer.render(
            { id: columnData.id, type: 'column', size },
            { empty: !columnData.elements || columnData.elements.length === 0 }
        );

        columnData.elements?.forEach(elementData => {
            const element = this.renderColumnElement(elementData);
            if (element) {
                columnElement.appendChild(element);
            }
        });

        return columnElement;
    }

    private renderColumnElement(elementData: IColumnElementData): HTMLElement | null {
        const renderOptions = (this.currentRuntimeContext || this.currentBaseUrl)
            ? { runtimeContext: this.currentRuntimeContext, baseUrl: this.currentBaseUrl }
            : undefined;

        const elementType = (elementData.type as string).toLowerCase();

        if (elementType === 'field') {
            const fieldData = elementData.data as FieldJsonData;
            const fieldType = fieldData.type as string;
            const fieldTypeLower = fieldType.toLowerCase();

            if (fieldTypeLower === 'captcha' || fieldTypeLower === 'gdpr' || fieldTypeLower === 'accountid') {
                const specialType = fieldTypeLower === 'accountid' ? 'accountId' : fieldTypeLower;
                const renderer = this.specialRendererFactory.getRenderer(specialType as 'captcha' | 'gdpr' | 'accountId');
                return renderer.render(fieldData as any, renderOptions);
            }

            const renderer = this.fieldRendererFactory.getRenderer(fieldType);
            return renderer.render(fieldData, renderOptions);
        } else if (elementType === 'widget') {
            const widgetData = elementData.data as WidgetJsonData;
            const renderer = this.widgetRendererFactory.getRenderer(widgetData.type);
            return renderer.render(widgetData, renderOptions);
        }

        console.warn('[TruForms] Unknown element type:', elementData.type);
        return null;
    }

    private renderHiddenFieldsBlock(hiddenFieldsData?: IHiddenFieldsData): HTMLElement | null {
        if (!hiddenFieldsData?.fields?.length) {
            return null;
        }

        const container = this.hiddenBlockRenderer.render();
        hiddenFieldsData.fields.forEach(fieldData => {
            const fieldElement = this.hiddenFieldRenderer.render(fieldData);
            container.appendChild(fieldElement);
        });

        return container;
    }
}
